import axios, { AxiosResponse } from 'axios';
import xorBy from 'lodash/xorBy';
import pick from 'lodash/pick';
import difference from 'lodash/difference';

import { BaseService } from './BaseService';
import { OmitId, OptionalId } from '~/utils/types';

import {
    CandidateSchool,
    StudyLevel,
    StudyField,
    StudyProgram,
    CandidateStudyProgram,
    Campus,
    UpsertStudyProgramVariables,
} from './types/csp';

export * from './types/csp';

// Utility to
export const isCspEqual = (a: CandidateStudyProgram, b: CandidateStudyProgram) => {
    const props = ['id', 'candidateId', 'programId', 'campusId', 'start', 'end'];

    const cleanA = pick(a, props);
    const cleanB = pick(b, props);

    return JSON.stringify(cleanA) === JSON.stringify(cleanB);
};

export const hasCspChanged = (csp: CandidateStudyProgram, oldCsps: CandidateStudyProgram[]) => {
    if (!csp.id) return true;

    const oldCsp = oldCsps.find(c => c.id === csp.id);
    if (!oldCsp) {
        return true;
    }

    return !isCspEqual(csp, oldCsp);
};

export default class CSPService extends BaseService {
    constructor() {
        super('candidate');
    }

    async getSchools(): Promise<CandidateSchool[]> {
        return axios({
            method: 'GET',
            url: this.getUrl('school', {}),
        }).then((res: AxiosResponse<CandidateSchool[]>) => res.data);
    }

    async getStudyLevels(): Promise<StudyLevel[]> {
        return axios({
            method: 'GET',
            url: this.getUrl('study-levels', {}),
        }).then((res: AxiosResponse<StudyLevel[]>) => res.data);
    }

    async getStudyFields(): Promise<StudyField[]> {
        return axios({
            method: 'GET',
            url: this.getUrl('study-fields', {}),
        }).then((res: AxiosResponse<StudyField[]>) => res.data);
    }

    async getSchoolCampuses(id: number): Promise<Campus[]> {
        return axios({
            method: 'GET',
            url: this.getUrl(`campuses/${id}`, {}),
        }).then((res: AxiosResponse<Campus[]>) => res.data);
    }

    async getSchoolStudyPrograms(schoolId: number): Promise<StudyProgram[]> {
        return axios({
            method: 'GET',
            url: this.getUrl(`study-programs/school/${schoolId}`, {}),
        }).then((res: AxiosResponse<StudyProgram[]>) => res.data);
    }

    async getCandidateStudyPrograms(): Promise<CandidateStudyProgram[]> {
        return axios({
            method: 'GET',
            url: this.getUrl('csp', {}),
        }).then((res: AxiosResponse<CandidateStudyProgram[]>) => res.data);
    }

    async deleteStudyProgram(id: string): Promise<boolean> {
        return axios({
            method: 'DELETE',
            url: this.getUrl(`study-program/${id}`, {}),
        }).then(this.isOK);
    }

    // Upsert a unverified study program
    async upsertStudyProgram(data: UpsertStudyProgramVariables): Promise<StudyProgram> {
        const body: UpsertStudyProgramVariables = {
            id: data.id,
            schoolId: data.schoolId,
            levelId: data.levelId,
            name: data.name,
            description: data.description,
            customSchoolName: data.customSchoolName,
            isVerified: false,
            addCampuses: data.addCampuses,
            removeCampuses: data.removeCampuses,
            addFields: data.addFields,
            removeFields: data.removeFields,
        };

        return axios({
            method: 'POST',
            url: this.getUrl('study-program', {}),
            data: body,
        }).then((res: AxiosResponse<StudyProgram>) => res.data);
    }

    async createCandidateStudyProgram(
        csp: OmitId<CandidateStudyProgram>
    ): Promise<CandidateStudyProgram> {
        return axios({
            method: 'POST',
            url: this.getUrl('csp', {}),
            data: csp,
        }).then((res: AxiosResponse<CandidateStudyProgram>) => res.data);
    }

    async upsertCandidateStudyProgram(
        csp: Omit<OptionalId<CandidateStudyProgram>, 'studyProgram'>
    ): Promise<CandidateStudyProgram> {
        return axios({
            method: 'POST',
            url: this.getUrl('csp', {}),
            data: csp,
        }).then((res: AxiosResponse<CandidateStudyProgram>) => res.data);
    }

    async saveStudyProgram(
        studyProgram: StudyProgram,
        oldStudyProgram?: StudyProgram
    ): Promise<StudyProgram | null> {
        const isVerified =
            !studyProgram.customSchoolName &&
            studyProgram.schoolId !== 0 &&
            studyProgram.schoolId !== undefined &&
            studyProgram.isVerified;

        const hasChanged = JSON.stringify(studyProgram) !== JSON.stringify(oldStudyProgram);

        if (isVerified || !hasChanged) {
            // Don't create verified study programs
            // and don't save unchanged study programs
            return Promise.resolve(null);
        }

        const fieldsToRemove = !oldStudyProgram
            ? []
            : difference(
                  (oldStudyProgram.fields || []).map(f => f.id),
                  (studyProgram.fields || []).map(f => f.id)
              );

        // This is a new unverified study program. Just call the endpoint
        // OR we have to create a new one because the isVerified changed
        const upsertReq = {
            id: studyProgram.id || undefined,
            schoolId: studyProgram.schoolId || undefined,
            levelId: studyProgram.levelId,
            name: studyProgram.name,
            description: studyProgram.description,
            customSchoolName: !!studyProgram.schoolId
                ? undefined
                : studyProgram.customSchoolName || undefined,

            // We should never create verified study programs from here
            isVerified: false,

            addFields: (studyProgram.fields ? studyProgram.fields : []).map(field => field.id),
            addCampuses: [],

            removeFields: fieldsToRemove,
            removeCampuses: [],
        };
        return await this.upsertStudyProgram(upsertReq);
    }

    async saveCandidateStudyProgram(
        csp: CandidateStudyProgram,
        oldCsp?: CandidateStudyProgram
    ): Promise<CandidateStudyProgram> {
        // Is this something we have to make unverified?
        // const isVerified = csp.schoolId && csp.schoolId > 0;
        const programOrNull = await this.saveStudyProgram(
            csp.studyProgram,
            oldCsp ? oldCsp.studyProgram : undefined
        );

        const newCsp: CandidateStudyProgram = {
            ...csp,
            programId: programOrNull !== null ? programOrNull.id : csp.programId,
        };

        if (oldCsp && oldCsp.programId !== newCsp.programId) {
            return await this.upsertCandidateStudyProgram(newCsp);
        }

        return await this.upsertCandidateStudyProgram(newCsp);
    }

    async saveCandidateStudyPrograms(
        csps: CandidateStudyProgram[],
        oldCsps: CandidateStudyProgram[]
    ): Promise<boolean> {
        const errors: string[] = [];

        // Create/update
        for (const csp of csps) {
            if (hasCspChanged(csp, oldCsps)) {
                const oldCsp = oldCsps.find(c => c.id === csp.id);
                const response = await this.saveCandidateStudyProgram(csp, oldCsp).catch(err => {
                    errors.push(err);
                });
            }
        }

        // Delete
        const deletedCsps = xorBy(
            csps.filter(c => !!c.id),
            oldCsps,
            'id'
        );
        for await (const csp of deletedCsps) {
            await this.deleteCandidateStudyProgram(csp.id);
        }

        return !errors.length ? Promise.resolve(true) : Promise.reject(errors);
    }

    async deleteCandidateStudyProgram(id: string): Promise<boolean> {
        return axios({
            method: 'DELETE',
            url: this.getUrl(`csp/${id}`, {}),
        }).then(this.isOK);
    }
}
